import React, { useState, useEffect, useRef } from "react";
import FieldElements from "../UniversalComponents/FieldElements";
import { pushAlert } from "../../utils/alertSlice";
import { getUserToken } from "../../utils/utilityFunctions/localServices";
import {
  useGetDropDownGroupQuery,
  useGetVendorByIdQuery,
  useLazyGetDropDownGroupValuesQuery,
  usePostGeneralInfoMutation,
} from "../../utils/customer/customerTaskTable";
import Button from "../UniversalComponents/DefaultButton";
import { useDispatch } from "react-redux";
import FileAttachment from "../UniversalComponents/FileAttachment";
import { hideLoader, showLoader } from "../../utils/loaderSlice";
import BackArrowCompoentIcon from "../../assets/images/icons/BackArrowCompoentIcon";
import { useLocation, useNavigate } from "react-router-dom";
import { LoaderCircle } from "lucide-react";

const VendorGeneralInfo = ({
  setVendorUUID,
  editData,
  handleBackButtonClick,
  isEditVendor,
  refetchVendorHeader,
  refetchVendorValues,
  setIsDisable,
  vendorName,
  setVendorName,
  setSelectedVendorData,
  vendorUUID,
  setVendorData,
  editSection,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const isViewModeFromState = location.state?.isViewMode ?? true;
  const isEditModeFromState = location.state?.isEditMode ?? false;
  const [viewData, setViewData] = useState(null);
  const [isViewMode, setIsViewMode] = useState(isViewModeFromState);
  const [isEditing, setIsEditing] = useState(isEditModeFromState);
  const [formData, setFormData] = useState({
    vendor_name: "",
    vendor_display_name: "",
    website: "",
    business_category: "",
    country: "",
    business_id: "",
    currency: "",
    vendor_logo: null,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [saasLogoUrl, setSaasLogoUrl] = useState(null);
  const [allGroups, setAllGroups] = useState([]);
  const [newlyCreatedVendorId, setNewlyCreatedVendorId] = useState(null);
  const isEditMode = location.pathname === "/edit-vendor";
  const isAddMode = location.pathname === "/add-vendor";
  const successAlertRef = useRef(false);
  const lastFetchedVendorIdRef = useRef(null);
  const modeRef = useRef({ isViewMode, isEditing });
  const viewModeLockRef = useRef(false);
  const refetchTriggeredRef = useRef(false);
  const renderCountRef = useRef(0);

  console.log("VendorGeneralInfo: Component mount", {
    pathname: location.pathname,
    isViewMode,
    isEditing,
    locationState: location.state,
    editData,
    vendorUUID,
  });

  const [
    postGeneralInfo,
    {
      data: generalInfoResponse,
      isError: isGeneralInfoError,
      isSuccess: isGeneralInfoSuccess,
      isLoading: isGeneralInfoLoading,
    },
  ] = usePostGeneralInfoMutation();
  const token = getUserToken();

  const { data: getGroups, isSuccess } = useGetDropDownGroupQuery(token);

  const vendorId =
    editData?.row_id || editData?.id || location?.state?.vendorData?.id || vendorUUID || newlyCreatedVendorId;
  const {
    data: vendorDetailsResponse,
    isLoading: isVendorLoading,
    isSuccess: isVendorSuccess,
    refetch: refetchVendorDetails,
    isUninitialized,
  } = useGetVendorByIdQuery({ UUID: vendorId, token }, { skip: !vendorId || vendorId === "undefined" });

  const { data: newVendorData, isSuccess: isNewVendorFetched } = useGetVendorByIdQuery(
    { UUID: newlyCreatedVendorId || vendorUUID, token },
    {
      skip: (!newlyCreatedVendorId && !vendorUUID) || newlyCreatedVendorId === lastFetchedVendorIdRef.current,
    }
  );

  // Initialize formData and viewData when API data is fetched
  useEffect(() => {
    if (viewModeLockRef.current) {
      console.log("VendorGeneralInfo: View mode locked, skipping initialization");
      return;
    }

    console.log("VendorGeneralInfo: Initializing with vendorDetailsResponse", { vendorDetailsResponse, vendorId });

    if (isVendorSuccess && vendorDetailsResponse?.data) {
      const vendorData = vendorDetailsResponse.data;
      const data = {
        id: vendorData.id || "",
        vendor_name: vendorData.vendor_name || "",
        vendor_display_name: vendorData.vendor_display_name || "",
        website: vendorData.website || "",
        business_category: vendorData.business_category || "",
        country: vendorData.country || "",
        business_id: vendorData.business_id || "",
        currency: vendorData.currency || "",
        vendor_logo: vendorData.vendor_logo_url || null,
      };
      console.log("VendorGeneralInfo: Setting vendorData from API", { vendorData: data });
      setFormData(data);
      setViewData(data);
      setSaasLogoUrl(data.vendor_logo || null);
      setVendorName(data.vendor_name || "");
      setSelectedVendorData(data);
      if (typeof setVendorData === "function") {
        setVendorData(data);
      }
      const newViewMode = isEditMode ? false : isViewModeFromState;
      const newEditing = isEditMode ? true : isEditModeFromState;
      setIsViewMode(newViewMode);
      setIsEditing(newEditing);
      modeRef.current = { isViewMode: newViewMode, isEditing: newEditing };
      console.log("VendorGeneralInfo: Post-init state", { isViewMode: newViewMode, isEditing: newEditing });
    } else if (!vendorId && !location.state?.vendorData && !editData) {
      console.log("VendorGeneralInfo: Resetting to empty form due to no vendorId or initial data");
      setFormData({
        vendor_name: "",
        vendor_display_name: "",
        website: "",
        business_category: "",
        country: "",
        business_id: "",
        currency: "",
        vendor_logo: null,
      });
      setViewData(null);
      setSaasLogoUrl(null);
      setVendorName("");
      setSelectedVendorData(null);
      setIsViewMode(isAddMode ? false : isViewModeFromState);
      setIsEditing(isAddMode ? true : isEditModeFromState);
      modeRef.current = {
        isViewMode: isAddMode ? false : isViewModeFromState,
        isEditing: isAddMode ? true : isEditModeFromState,
      };
    } else if (location.state?.vendorData || editData) {
      const initialData = location.state?.vendorData || editData;
      const data = {
        id: initialData.id || "",
        vendor_name: initialData.vendor_name || "",
        vendor_display_name: initialData.vendor_display_name || "",
        website: initialData.website || "",
        business_category: initialData.business_category || "",
        country: initialData.country || "",
        business_id: initialData.business_id || "",
        currency: initialData.currency || "",
        vendor_logo: initialData.vendor_logo_url || null,
      };
      console.log("VendorGeneralInfo: Setting vendorData from initial data", { vendorData: data });
      setFormData(data);
      setViewData(data);
      setSaasLogoUrl(data.vendor_logo || null);
      setVendorName(data.vendor_name || "");
      setSelectedVendorData(data);
      if (typeof setVendorData === "function") {
        setVendorData(data);
      }
      const newViewMode = isEditMode ? false : isViewModeFromState;
      const newEditing = isEditMode ? true : isEditModeFromState;
      setIsViewMode(newViewMode);
      setIsEditing(newEditing);
      modeRef.current = { isViewMode: newViewMode, isEditing: newEditing };
      console.log("VendorGeneralInfo: Post-init state", { isViewMode: newViewMode, isEditing: newEditing });
    }
  }, [
    isVendorSuccess,
    vendorDetailsResponse,
    editData,
    location.state?.vendorData,
    isViewModeFromState,
    isEditModeFromState,
    setVendorName,
    setSelectedVendorData,
    setVendorData,
    vendorId,
    isAddMode,
    isEditMode,
    viewModeLockRef,
  ]);

  // Handle dropdown groups
  useEffect(() => {
    if (isSuccess) {
      if (!getGroups?.data?.length) {
        dispatch(pushAlert({ message: "No Groups Found", type: "warning", visible: true }));
      }
      setAllGroups(getGroups?.data || []);
    }
  }, [isSuccess, getGroups, dispatch]);

  // Reset refetchTriggeredRef when isGeneralInfoSuccess changes to false
  useEffect(() => {
    if (!isGeneralInfoSuccess) {
      refetchTriggeredRef.current = false;
    }
  }, [isGeneralInfoSuccess]);

  // Handle save success and schedule refetch
  useEffect(() => {
    if (isGeneralInfoLoading) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }

    if (isGeneralInfoSuccess && !successAlertRef.current) {
      console.log("VendorGeneralInfo: Save success", { generalInfoResponse, vendorId });
      successAlertRef.current = true;
      dispatch(
        pushAlert({
          message: generalInfoResponse?.message || "Vendor saved successfully",
          type: "success",
          visible: true,
        })
      );

      const newVendorUUID = generalInfoResponse?.data?.id || generalInfoResponse?.data;
      if (newVendorUUID && newVendorUUID !== "undefined") {
        viewModeLockRef.current = true;
        lastFetchedVendorIdRef.current = newVendorUUID;
        setVendorUUID(newVendorUUID);
        setNewlyCreatedVendorId(newVendorUUID);
        // Only refetch if the query is initialized and not skipped
        if (!isUninitialized && vendorId) {
          console.log("VendorGeneralInfo: Triggering refetchVendorDetails after save", { vendorId: newVendorUUID });
          refetchVendorDetails();
        }
      } else {
        console.warn("VendorGeneralInfo: Invalid newVendorUUID", { newVendorUUID, generalInfoResponse });
        dispatch(
          pushAlert({
            message: "Failed to retrieve valid vendor ID after save",
            type: "danger",
            visible: true,
          })
        );
      }
      setImagePreview(null);
    }

    if (isGeneralInfoError) {
      console.log("VendorGeneralInfo: Save error", { generalInfoResponse });
      dispatch(
        pushAlert({
          message: generalInfoResponse?.message || "Failed to save vendor info",
          type: "danger",
          visible: true,
        })
      );
    }
  }, [
    isGeneralInfoLoading,
    isGeneralInfoSuccess,
    isGeneralInfoError,
    generalInfoResponse,
    setVendorUUID,
    isUninitialized,
    vendorId,
  ]);

  // Update state with refetched vendor data after save
  useEffect(() => {
    if (isGeneralInfoSuccess && isVendorSuccess && vendorDetailsResponse?.data) {
      const vendorData = vendorDetailsResponse.data;
      const data = {
        id: vendorData.id || "",
        vendor_name: vendorData.vendor_name || "",
        vendor_display_name: vendorData.vendor_display_name || "",
        website: vendorData.website || "",
        business_category: vendorData.business_category || "",
        country: vendorData.country || "",
        business_id: vendorData.business_id || "",
        currency: vendorData.currency || "",
        vendor_logo: vendorData.vendor_logo_url || null,
      };
      console.log("VendorGeneralInfo: Updating with refetched data after save", { vendorData: data });
      setViewData(data);
      setFormData(data);
      setSaasLogoUrl(data.vendor_logo || null);
      setVendorName(data.vendor_name || "");
      setSelectedVendorData(data);
      if (typeof setVendorData === "function") {
        setVendorData(data);
      }
      setIsDisable(false);
      setIsViewMode(true);
      setIsEditing(false);
      modeRef.current = { isViewMode: true, isEditing: false };
      console.log("VendorGeneralInfo: Post-save state", { isViewMode: true, isEditing: false, vendorData: data });

      navigate(location.pathname, {
        state: {
          ...location.state,
          vendorData: data,
          isViewMode: true,
          isEditMode: false,
        },
        replace: true,
      });
      refetchVendorHeader();
      refetchVendorValues();
    }
  }, [
    isGeneralInfoSuccess,
    isVendorSuccess,
    vendorDetailsResponse,
    setVendorName,
    setSelectedVendorData,
    setVendorData,
    navigate,
    setIsDisable,
  ]);

  // Handle new vendor data fetch
  useEffect(() => {
    if (isNewVendorFetched && newVendorData?.status && newVendorData?.data) {
      console.log("VendorGeneralInfo: New Vendor Data Fetched", { vendor: newVendorData.data });
      viewModeLockRef.current = true;
      const vendor = newVendorData.data;
      const vendorData = {
        id: vendor.id || "",
        vendor_name: vendor?.vendor_name || "",
        vendor_display_name: vendor?.vendor_display_name || "",
        website: vendor?.website || "",
        business_category: vendor?.business_category || "",
        country: vendor?.country || "",
        business_id: vendor?.business_id || "",
        currency: vendor?.currency || "",
        vendor_logo: vendor?.vendor_logo_url || null,
      };
      setVendorName(vendor?.vendor_name);
      setViewData(vendorData);
      setSaasLogoUrl(vendor?.vendor_logo_url || null);
      setFormData(vendorData);
      if (typeof setVendorData === "function") {
        setVendorData(vendor);
      }
      setSelectedVendorData(vendor);
      setIsViewMode(true);
      setIsEditing(false);
      modeRef.current = { isViewMode: true, isEditing: false };
      console.log("VendorGeneralInfo: Post-fetch state", { isViewMode: true, isEditing: false, vendorData });
      navigate(location.pathname, {
        state: {
          ...location.state,
          vendorData: vendor,
          isViewMode: true,
          isEditMode: false,
        },
        replace: true,
      });
    }
  }, [isNewVendorFetched, newVendorData, setVendorName, setVendorData, setSelectedVendorData, navigate, location]);

  // Refetch vendor details
  useEffect(() => {
    if (vendorId && !isVendorLoading && vendorId !== "undefined" && !isUninitialized) {
      console.log("VendorGeneralInfo: Triggering refetchVendorDetails", { vendorId });
      refetchVendorDetails();
    }
  }, [vendorId, isVendorLoading, refetchVendorDetails, isUninitialized]);

  const VendorHeader = [
    {
      label: "Name",
      id: "vendor_name",
      type: "text",
      placeholder: "Enter Name",
      isMandatory: true,
      disabled: editData && formData.vendor_name && !isEditing,
    },
    {
      label: "Display Name",
      id: "vendor_display_name",
      type: "text",
      placeholder: "Enter Display Name",
      disabled: editData && formData.vendor_display_name && !isEditing,
    },
    {
      label: "Website",
      id: "website",
      type: "text",
      placeholder: "Enter Website",
      disabled: editData && formData.website && !isEditing,
    },
    {
      label: "Business Category",
      id: "business_category",
      type: "select",
      useFetchHook: useLazyGetDropDownGroupValuesQuery,
      hookPayload: { field_name: "business_category" },
      disabled: editData && formData.business_category && !isEditing,
    },
    {
      label: "Country",
      id: "country",
      type: "select",
      useFetchHook: useLazyGetDropDownGroupValuesQuery,
      hookPayload: { field_name: "country" },
      disabled: editData && formData.country && !isEditing,
    },
    {
      label: "Social Security Number",
      id: "business_id",
      type: "text",
      placeholder: "Enter Number",
      disabled: editData && formData.business_id && !isEditing,
    },
    {
      label: "Currency",
      id: "currency",
      type: "select",
      useFetchHook: useLazyGetDropDownGroupValuesQuery,
      hookPayload: { field_name: "currency" },
      disabled: editData && formData.currency && !isEditing,
    },
  ];

  const handleGeneralInfoSave = (formData) => {
    const vendorData = {
      vendor_name: formData.vendor_name,
      vendor_display_name: formData.vendor_display_name || "",
      website: formData.website || "",
      business_category: formData.business_category || "",
      country: formData.country || "",
      business_id: formData.business_id || "",
      currency: formData.currency || "",
    };

    if (vendorId && vendorId !== "undefined") {
      vendorData.id = vendorId;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("data", JSON.stringify(vendorData));
    if (formData.vendor_logo instanceof File) {
      formDataToSend.append("vendor_logo", formData.vendor_logo, formData.vendor_logo.name);
    }

    postGeneralInfo({ token, formData: formDataToSend });
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (file) => {
    if (!file) return;

    const isValidType = ["image/jpeg", "image/png"].includes(file.type);
    if (!isValidType) {
      dispatch(pushAlert({ message: "Only PNG and JPEG formats are supported.", type: "warning", visible: true }));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const arrayBuffer = reader.result;
      const blob = new Blob([arrayBuffer], { type: file.type });
      const logoFile = new File([blob], file.name, { type: file.type });

      setFormData((prev) => ({
        ...prev,
        vendor_logo: logoFile,
      }));
      setImagePreview(URL.createObjectURL(file));
      setSaasLogoUrl(null);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFileChange(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFileChange(file);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.vendor_name) {
      dispatch(pushAlert({ message: "Vendor Name is mandatory.", type: "warning", visible: true }));
      return;
    }

    // Compare formData with viewData or vendorDetailsResponse
    const comparisonData = viewData ||
      (vendorDetailsResponse?.data ? vendorDetailsResponse.data : null) || {
        vendor_name: "",
        vendor_display_name: "",
        website: "",
        business_category: "",
        country: "",
        business_id: "",
        currency: "",
        vendor_logo: null,
      };
    const hasChanges =
      formData.vendor_name !== comparisonData.vendor_name ||
      formData.vendor_display_name !== comparisonData.vendor_display_name ||
      formData.website !== comparisonData.website ||
      formData.business_category !== comparisonData.business_category ||
      formData.country !== comparisonData.country ||
      formData.business_id !== comparisonData.business_id ||
      formData.currency !== comparisonData.currency ||
      (formData.vendor_logo instanceof File && formData.vendor_logo !== comparisonData.vendor_logo);

    console.log("VendorGeneralInfo: Checking for changes", { formData, comparisonData, hasChanges });

    if (!hasChanges) {
      dispatch(
        pushAlert({
          message: "Nothing is changed",
          type: "warning",
          visible: true,
        })
      );
      return;
    }

    successAlertRef.current = false;
    viewModeLockRef.current = false;
    refetchTriggeredRef.current = false;
    handleGeneralInfoSave(formData);
  };

  const handleCancel = () => {
    console.log("VendorGeneralInfo: Cancel button clicked", {
      viewData,
      formData,
      isViewMode,
      isEditing,
      vendorDetailsResponse,
      locationState: location.state,
      isAddMode,
      isEditMode,
    });
    viewModeLockRef.current = false;

    // Fallback data for view mode, prioritizing vendorDetailsResponse or editData
    const fallbackData = (vendorDetailsResponse?.data ? vendorDetailsResponse.data : null) ||
      viewData ||
      editData ||
      location.state?.vendorData || {
        vendor_name: "",
        vendor_display_name: "",
        website: "",
        business_category: "",
        country: "",
        business_id: "",
        currency: "",
        vendor_logo: null,
      };
    const resetData = {
      vendor_name: fallbackData.vendor_name || "",
      vendor_display_name: fallbackData.vendor_display_name || "",
      website: fallbackData.website || "",
      business_category: fallbackData.business_category || "",
      country: fallbackData.country || "",
      business_id: fallbackData.business_id || "",
      currency: fallbackData.currency || "",
      vendor_logo: null,
    };

    console.log("VendorGeneralInfo: Resetting formData", { resetData, fallbackData });

    // Update state
    setFormData(resetData);
    setViewData(fallbackData); // Ensure viewData is set for view mode
    setImagePreview(null);
    setSaasLogoUrl(fallbackData.vendor_logo || null);
    setVendorName(resetData.vendor_name);
    setSelectedVendorData(resetData);
    if (typeof setVendorData === "function") {
      setVendorData(resetData);
    }
    setIsViewMode(true);
    setIsEditing(false);
    modeRef.current = { isViewMode: true, isEditing: false };
    setIsDisable(false);

    if (isAddMode && !viewData) {
      console.log("VendorGeneralInfo: Cancel in add mode with no viewData, navigating to /vendors");
      navigate("/vendors", { state: { vendorData: null, isViewMode: true } });
    } else {
      console.log("VendorGeneralInfo: Cancel in edit mode or add mode with viewData, switching to view mode");
      navigate(location.pathname, {
        state: {
          ...location.state,
          isViewMode: true,
          isEditMode: false,
          vendorData: fallbackData,
        },
        replace: true,
      });
    }

    console.log("VendorGeneralInfo: Post-cancel state", {
      isViewMode: true,
      isEditing: false,
      formData: resetData,
      viewData: fallbackData,
      fallbackData,
    });
  };

  const infoFields = [
    { label: "Name", value: viewData?.vendor_name },
    { label: "Display Name", value: viewData?.vendor_display_name },
    { label: "Website", value: viewData?.website, isLink: true },
    { label: "Business Category", value: viewData?.business_category },
    { label: "Country", value: viewData?.country },
    { label: "Social Security Number", value: viewData?.business_id },
    { label: "Currency", value: viewData?.currency },
  ];

  const getVendorFields = ({ VendorFields }) => (
    <>
      {VendorFields?.map((eachField) => (
        <div key={eachField.id} className={`flex flex-row col-span-${eachField.colspan ?? "1"} items-center`}>
          {!eachField?.noLabel && (
            <label htmlFor={eachField?.label} className="block mb-1 text-12 font-400 text-[#666] w-1/3 items-center">
              <span className="text-sm">{eachField?.label}</span>
              {eachField?.isMandatory && <span className="text-red-700"> *</span>}
            </label>
          )}
          <div className="flex items-center min-w-0 grow">
            <FieldElements
              id={eachField?.id}
              name={eachField?.id}
              label={eachField?.label}
              placeholder={eachField?.placeholder}
              value={formData[eachField?.id] || ""}
              onChange={handleFieldChange}
              type={eachField?.type}
              formData={formData}
              useFetchHook={eachField?.useFetchHook}
              hookPayload={eachField?.hookPayload}
              labelKey={"label"}
              valueKey={"id"}
              valueKeyBased
              disabled={eachField?.disabled}
            />
          </div>
        </div>
      ))}
    </>
  );

  renderCountRef.current += 1;
  console.log("VendorGeneralInfo: Render", {
    isViewMode,
    isEditing,
    formData,
    viewData,
    modeRef: modeRef.current,
    viewModeLock: viewModeLockRef.current,
    renderCount: renderCountRef.current,
  });

  return (
    <div className="h-full">
      <div className="h-full flex flex-col">
        <div className="flex items-center px-2 py-2">
          <button
            onClick={() => {
              console.log("VendorGeneralInfo: Back button clicked", {
                pathname: location.pathname,
                vendorUUID,
                vendorData: viewData,
              });
              if (isAddMode) {
                navigate("/vendors", {
                  state: { vendorData: null, isViewMode: true },
                });
              } else {
                const navId = vendorId && vendorId !== "undefined" ? vendorId : vendorUUID;
                if (!navId || navId === "undefined") {
                  console.warn("VendorGeneralInfo: No valid vendorId for navigation", { vendorId, vendorUUID });
                  dispatch(
                    pushAlert({
                      message: "No valid vendor ID available for navigation",
                      type: "warning",
                      visible: true,
                    })
                  );
                  navigate("/vendors");
                  return;
                }
                navigate(`/vendor-profile/${navId}`, {
                  state: { isEditMode: false, vendorData: viewData || location.state?.vendorData, isViewMode: true },
                });
              }
            }}
          >
            <BackArrowCompoentIcon />
          </button>
          <h3 className="text-16 font-600 text-[#383838] mx-2 items-center">
            <span className="items-center text-[rgba(56,56,56,0.6)] text-16 font-500">Vendor &gt; </span>
            {vendorName || formData.vendor_name || "Add Vendor"}
          </h3>
        </div>
        {isViewMode ? (
          !viewData ? (
            <div className="text-[#999] p-4">No vendor data available to display</div>
          ) : (
            <div className="border rounded-md w-full">
              <div className="p-4 w-full border rounded-md bg-[#F8F9FA]">
                <div className="flex flex-col items-start gap-6">
                  <div className="">
                    {viewData?.vendor_logo ? (
                      <img
                        src={viewData.vendor_logo}
                        alt="Business Logo"
                        className="w-40 h-40 rounded bg-[#0a1d36] p-3 object-contain"
                      />
                    ) : (
                      <div className="w-36 h-36 rounded bg-gray-200 flex items-center justify-center text-10 font-500 text-[#666] text-center px-2">
                        <div className="space-y-1">
                          Drop your image here, or <br />
                          <span className="text-[#2999BC]">Browse</span>
                          <br />
                          Supports: PNG, JPEG
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap w-full gap-y-4 text-sm">
                    <div className="w-1/2 space-y-4">
                      {infoFields
                        .filter((field) => ["Name", "Website", "Country", "Currency"].includes(field.label))
                        .map((field, index) => (
                          <div key={index} className="flex">
                            <span className="w-[30%] font-500 text-[#666] text-14 items-center flex">
                              {field.label} :{" "}
                            </span>
                            <span
                              className={`w-[70%] font-400 text-[#666] text-12 items-center flex ${field.isLink ? "" : ""}`}
                            >
                              {field.value || "--"}
                            </span>
                          </div>
                        ))}
                    </div>
                    <div className="w-1/2 space-y-4">
                      {infoFields
                        .filter((field) =>
                          ["Display Name", "Business Category", "Social Security Number"].includes(field.label)
                        )
                        .map((field, index) => (
                          <div key={index} className="flex">
                            <span className="w-[40%] font-500 text-[#666] text-14">{field.label} : </span>
                            <span className={`w-[60%] font-400 text-[#666] text-12 ${field.isLink ? " " : ""}`}>
                              {field.value || "--"}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                  <div>
                    <Button
                      variant="primary"
                      className="mt-4"
                      onClick={() => {
                        console.log("VendorGeneralInfo: Edit button clicked", {
                          viewData,
                          isViewMode,
                          isEditing,
                          formData,
                        });
                        viewModeLockRef.current = false;
                        setFormData(viewData); // Pre-fill edit form with current viewData
                        setSaasLogoUrl(viewData?.vendor_logo || null);
                        setImagePreview(null);
                        setIsViewMode(false);
                        setIsEditing(true);
                        modeRef.current = { isViewMode: false, isEditing: true };
                        console.log("VendorGeneralInfo: Post-edit state", {
                          isViewMode: false,
                          isEditing: true,
                          formData,
                        });
                        navigate(location.pathname, {
                          state: {
                            ...location.state,
                            isViewMode: false,
                            isEditMode: true,
                            vendorData: viewData,
                          },
                          replace: true,
                        });
                      }}
                    >
                      Edit
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 min-h-0 grow flex flex-col justify-between">
            <div className="flex flex-col gap-4 w-full border border-[#D2DAE1] rounded-md p-4">
              <div className="w-full">
                <div
                  onClick={() => document.getElementById("hiddenFileInput").click()}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  className="w-40 h-40 border-2 border-dashed border-gray-400 rounded-md flex items-center justify-center text-center cursor-pointer hover:bg-gray-50"
                >
                  {imagePreview || saasLogoUrl ? (
                    <img
                      src={imagePreview || saasLogoUrl}
                      alt="Vendor Logo"
                      className="object-contain w-full h-full rounded-md p-2 bg-white"
                    />
                  ) : (
                    <div className="text-gray-500 text-sm">
                      Click or Drag & Drop here
                      <br />
                      <span className="text-xs text-gray-400">Supported formats: PNG, JPEG</span>
                    </div>
                  )}
                  <input
                    type="file"
                    id="hiddenFileInput"
                    accept="image/png, image/jpeg"
                    onChange={handleFileInputChange}
                    className="hidden"
                    key={imagePreview || "fileInput"}
                  />
                </div>
              </div>
              <div className="min-w-0 grow">
                <div className="grid grid-cols-2 gap-x-40 gap-y-4">
                  {getVendorFields({ VendorFields: VendorHeader })}
                </div>
              </div>
              <div className="pt-4 gap-3 flex">
                <Button variant="secondary" onClick={handleCancel} className="whitespace-nowrap px-4">
                  CANCEL
                </Button>
                {editData || vendorUUID ? (
                  <Button variant="primary" type="submit">
                    UPDATE
                  </Button>
                ) : (
                  <Button variant="primary" type="submit">
                    SAVE
                  </Button>
                )}
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default VendorGeneralInfo;
